<template>
	<div style='width: 100%'>
		<div 
			class="agenda-card-wrapper d-flex justify-space-between align-center px-4 pb-4 mb-4"
			v-bind:style="{paddingTop: agendaEvent.sponsored_by ? '37px' : '16px'}"
		>
			<div>
				<div v-if='agendaEvent.sponsored_by' class="event-card__pin">
					<span>
						<img :src='sponsoredStarIcon' alt='iconSponsored' class='mr-1' />
						{{ sponsoredLabel }}
					</span>
				</div>
				<div 
					class="agenda-card-description"
					v-bind:style="{color: isAgendaPast ? '#AAA' : 'var(--accent-color)'}"
				>
					{{ agendaEvent.events_agenda_text }}
				</div>
				<div 
					class="agenda-card-date"
					v-bind:style="{color: isAgendaPast ? '#AAA' : 'var(--accent-color)'}"
				>
					{{ formatDateRange(agendaEvent.events_agenda_from, agendaEvent.events_agenda_to, agendaEvent.agendaTimezone) }}
				</div>
				<div 
					class="agenda-card-location" 
					v-if="agendaEvent.session_location"
					v-bind:style="{color: isAgendaPast ? '#AAA' : 'var(--accent-color)'}"
				>
					<v-icon x-small v-bind:style="{color: isAgendaPast ? '#AAA !important' : 'var(--button-secondary-background-color) !important'}">
						mdi-map-marker
					</v-icon>
					{{ agendaEvent.session_location }}
				</div>
				<div 
					v-for="{ track_color, track_id, track_name } in agendaEvent.tracks" 
					:key="track_id"
					class="agenda-card-tag" 
					:style="`color: ${track_color}`"
				>
					<v-icon :color="track_color" dark small class="mr-1">
						mdi-circle
					</v-icon>
					{{ track_name }}
				</div>
			</div>
			<div>
				<v-btn 
					@click.prevent="toggleMyAgenda" 
					class="agenda-card-schedule-btn elevation-0"
					:loading="isMyAgendaLoading && myAgendaIdIsLoading === agendaEvent.events_agenda_id"
				>
					<v-icon 
						class="agenda-card-schedule-icon"
						v-bind:style="{ color: `${scheduleIcon.color} !important` }"
					>
						{{ scheduleIcon.icon }}
					</v-icon>
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import sponsoredStarIcon from '@/assets/images/icoт-sponsored-star.svg'

Vue.use(VueScrollTo)

export default {
	name: 'AgendaCard',

	props: {
		agendaEvent: {
			type: Object,
			default: () => {},
		},
	},

	data() {
		return {
			sponsoredStarIcon
		}
	},

	methods: {
		...mapActions('event', ['addMyAgenda', 'removeMyAgenda']),

		formatDateRange(dateFrom, dateTo, timezone) {
			const options = {
				weekday: 'long',
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC'
			}

			//Convert Unix timestamps to JavaScript Date objects
			const fromDate = new Date(dateFrom * 1000)
			const toDate = new Date(dateTo * 1000)
			const timezoneOffset = Number(timezone.timezone_utc.slice(-2))
			const action = timezone.timezone_utc.slice(3, 4)
			if (action === '-') {
				fromDate.setHours(fromDate.getHours() - timezoneOffset)
				toDate.setHours(toDate.getHours() - timezoneOffset)
			} else {
				fromDate.setHours(fromDate.getHours() + timezoneOffset)
				toDate.setHours(toDate.getHours() + timezoneOffset)
			}

			// Format the dates
			const formattedFromDate = fromDate.toLocaleString('en-US', options)
			const formattedToDate = toDate.toLocaleTimeString('en-US', options)

			// Construct the formatted date range string
			return `${formattedFromDate} - ${formattedToDate}`
		},
		toggleMyAgenda() {
			if (!this.isMyAgendaLoading) {
				if (this.agendaEvent.is_scheduled) {
					this.removeMyAgenda({id: this.agendaEvent.events_agenda_id, myAgenda: this.$route.name === 'my-agenda-events', single: false})
				} else {
					this.addMyAgenda({id: this.agendaEvent.events_agenda_id, single: false})
				}
			}
		}
	},
	computed: {
		...mapGetters('event', ['isMyAgendaLoading', 'myAgendaIdIsLoading']),
		...mapGetters('settings', ['styles']),

		sponsoredLabel() {
			return this.agendaEvent.sponsored_by ? `Sponsored by ${this.agendaEvent.sponsored_by}` : ''
		},
		scheduleIcon() {
			return this.agendaEvent.is_scheduled ? { icon: 'mdi-calendar-check', color: '#9B9B9B' } : { icon: 'mdi-calendar-plus', color: this.styles.button_main_background }
		},
		isAgendaPast() {
			const date = new Date()
			const currentGMTDate = date.getTime() + (date.getTimezoneOffset() * 60 * 1000)
			const agendaTimezoneOffset = +this.agendaEvent.agendaTimezone.timezone_utc.slice(-2) * 60 * 60 * 1000
			const agendaTimeZoneOffsetDirection = this.agendaEvent.agendaTimezone.timezone_utc.slice(3, 4) === '+'
			const agendaDate = new Date(this.agendaEvent.events_agenda_to * 1000).getTime()
			const agendaGMTDate = agendaDate + (agendaTimeZoneOffsetDirection === '+' ? -agendaTimezoneOffset : +agendaTimezoneOffset)
			return currentGMTDate > agendaGMTDate
		}
	}
}
</script>

<style lang="scss">
.agenda-card {
	&-wrapper {
		background: rgb(251, 251, 251);
		border: 1px solid rgb(226, 226, 226);
		border-radius: 12px;
		position: relative;
	}

	&-date {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 600;
		line-height: 22px;
		margin-bottom: 4px;
	}

	&-description {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 17.07px;
		margin-bottom: 8px;
	}

	&-tag {
		display: flex;
		align-items: center;
		font-size: 13px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 19.37px;
	}

	&-location {
		display: flex;
		color: var(--button-secondary-background-color);
		font-size: 13px;
		font-family: Montserrat, sans-serif;
		font-weight: 500;
		line-height: 19.37px;
		margin-bottom: 4px;
	}
	&__pin {
		text-align: right;
		position: absolute;
		top: 0;
		right: 0;

		span {
			z-index: 0;
			position: relative;
			background: var(--badge-sponsored-background);
			display: inline-flex;
			padding: 5px 10px 5px 10px;
			border-top-right-radius: 8px;
			color: rgb(255, 255, 255);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}

		// Start
		span {
			&:before {
				z-index: -1;
				content: '';
				position: absolute;
				left: -8%;
				top: 0;
				height: 100%;
				width: 100%;
				background: var(--badge-sponsored-background);
				-webkit-transform: skewX(20deg);
				-moz-transform: skewX(20deg);
				-ms-transform: skewX(20deg);
				transform: skewX(20deg);
			}
		}
	}
	&-schedule {
		&-btn {
			background-color: transparent !important;
			padding: 0 8px !important;
			min-width: auto !important;

			i {
				color: z !important;
			}
		}
	}
}
</style>
